// В.2.d-1: keyword `immune` (Иммунитет) — общая фракционно-нейтральная
// механика. Используется Дроном-дефибриллятором (Лив Лайф, В.2.d-1) и в
// будущем активкой Зейна (В.2.h) и потенциально другими картами.
//
// Поведение (диздок 6.7):
//   - Цель с keyword `immune` поглощает ВЕСЬ урон за один раз. Любой источник
//     (атака, AoE-Действие, триггеры, пассивки) — после поглощения Иммунитет
//     снимается.
//   - Поглощение работает ДО Брони и ДО Пробития (immune ловит весь dmg).
//   - После снятия Иммунитета цель может получить урон от других источников
//     в той же Ф3.
//   - Источники Иммунитета хранятся стеком в card.immuneSources:
//     [{sourceId, expiresAtTurn|null}]. При поглощении урона очищается весь
//     стек (это «один outcome» — любая защита разовая). При истечении срока
//     одного источника — он удаляется индивидуально; если стек пуст, immune
//     keyword снимается. Молчание цели снимает Иммунитет (как любой
//     текстовый эффект — стек обнуляется).
//   - НЕ работает против лечения / баффов / дебаффов. Только урон.
//
// API:
//   applyImmunity(state, target, sourceId, expiresAtTurn=null)
//   removeImmunity(state, target, sourceId)
//   isImmune(target)
//   absorbDamageIfImmune(state, target, dmg, source) — возвращает true, если
//     Иммунитет поглотил урон (вызвавший должен пропустить дальнейшую
//     обработку), false — если урон проходит как обычно.
//   expireImmuneSources(state) — снимает истёкшие источники в Ф1.
//
// Лог при поглощении:
//   «🛡 имя_цели [сторона]: Иммунитет поглотил N урона от имя_источника»

import { hasKeyword, removeKeyword } from '../keywords.js';

export const IMMUNE_KW = 'immune';

function ensureSources(card) {
  if (!Array.isArray(card.immuneSources)) card.immuneSources = [];
  return card.immuneSources;
}

function ensureKeyword(card) {
  if (!Array.isArray(card.keywords)) card.keywords = [];
  if (!hasKeyword(card, IMMUNE_KW)) card.keywords.push(IMMUNE_KW);
}

function dropKeywordIfNoSources(card) {
  if (!card.immuneSources || card.immuneSources.length === 0) {
    removeKeyword(card, IMMUNE_KW);
  }
}

// C.1: явный shortcut для Иммунитета первого удара (Био-щит и т.п.).
// Просто делегирует в applyImmunity с expiresAtTurn=null — поглощает один
// удар и снимается. Импортируется как `applyImmuneFirstHit` чтобы карты
// могли явно выражать намерение.
export function applyImmuneFirstHit(state, target, sourceId = 'first_hit') {
  return applyImmunity(state, target, sourceId, null);
}

export function applyImmunity(state, target, sourceId = 'unknown', expiresAtTurn = null) {
  if (!target) return false;
  if (target.type !== 'unit' && target.type !== 'hero') return false;
  if (!target.isAlive || (target.type === 'unit' && !target.isAlive())) return false;
  if (target.type === 'hero' && (target.hp == null || target.hp <= 0)) return false;
  const sources = ensureSources(target);
  sources.push({ sourceId, expiresAtTurn: expiresAtTurn ?? null });
  ensureKeyword(target);
  if (state?.log) {
    const ownerName = state.players?.[target.ownerId]?.name || '?';
    const tail = expiresAtTurn != null ? ` до начала хода ${expiresAtTurn}` : '';
    state.log.add(
      `🛡 ${target.name_ru || target.id} [${ownerName}]: получает Иммунитет (источник: ${sourceId})${tail}`
    );
  }
  return true;
}

export function removeImmunity(state, target, sourceId) {
  if (!target?.immuneSources) return false;
  const before = target.immuneSources.length;
  target.immuneSources = target.immuneSources.filter(s => s.sourceId !== sourceId);
  const removed = before !== target.immuneSources.length;
  dropKeywordIfNoSources(target);
  return removed;
}

export function isImmune(target) {
  if (!target) return false;
  if (Array.isArray(target.immuneSources) && target.immuneSources.length > 0) return true;
  // Защитная ветка: если кто-то добавил 'immune' напрямую в base.keywords
  // (на В.2.d-1 такого не используется, но движок не должен ломаться).
  return hasKeyword(target, IMMUNE_KW);
}

// Поглощает весь урон. Поведение по типу источника (В.2.d-1-fix2):
//   - Источник с expiresAtTurn != null (длительный): остаётся активен до
//     истечения срока (expireImmuneSources в Ф1). Поглощает все удары в окне.
//   - Источник с expiresAtTurn === null (базовый, divine-shield): снимается
//     ПОСЛЕ одного поглощения.
// Если на цели одновременно есть и временные, и базовые источники — базовые
// снимаются после первого срабатывания, временные сохраняются. Эффект «есть
// immune прямо сейчас» по факту общий: пока хотя бы один источник остался —
// keyword 'immune' держится.
// Возвращает true, если урон был поглощён.
export function absorbDamageIfImmune(state, target, dmg, source = null) {
  if (!target || dmg <= 0) return false;
  if (!isImmune(target)) return false;
  // Снимаем только базовые (без expiresAtTurn) источники. Временные остаются.
  if (Array.isArray(target.immuneSources)) {
    target.immuneSources = target.immuneSources.filter(s => s.expiresAtTurn != null);
  }
  dropKeywordIfNoSources(target);
  if (state?.log) {
    const ownerName = state.players?.[target.ownerId]?.name || '?';
    const srcName = sourceLabel(source);
    const stillImmune = isImmune(target);
    const tail = stillImmune ? '' : ' (Иммунитет израсходован)';
    state.log.add(
      `🛡 ${target.name_ru || target.id} [${ownerName}]: Иммунитет поглотил ${dmg} урона от ${srcName}${tail}`
    );
  }
  return true;
}

// В Ф1 (phaseDraw) — снимаем источники с истёкшим expiresAtTurn.
// Логика «истёк»: state.currentTurn >= expiresAtTurn. Например, бафф,
// поставленный в ходу 4 с expiresAtTurn=6, в Ф1 хода 6 уйдёт.
export function expireImmuneSources(state) {
  if (!state?.players) return;
  for (const p of state.players) {
    const all = [];
    if (p.hero) all.push(p.hero);
    for (const u of p.unitsOnField()) all.push(u);
    for (const card of all) {
      if (!Array.isArray(card.immuneSources) || card.immuneSources.length === 0) continue;
      const surviving = [];
      for (const s of card.immuneSources) {
        if (s.expiresAtTurn != null && state.currentTurn >= s.expiresAtTurn) {
          if (state.log) {
            const ownerName = state.players?.[card.ownerId]?.name || '?';
            state.log.add(
              `🛡 ${card.name_ru || card.id} [${ownerName}]: Иммунитет (источник ${s.sourceId}) истёк`
            );
          }
          continue;
        }
        surviving.push(s);
      }
      card.immuneSources = surviving;
      dropKeywordIfNoSources(card);
    }
  }
}

// Молчание цели — снимает все источники Иммунитета. Зовётся из silence.js.
export function clearImmunityOnSilence(state, card) {
  if (!card?.immuneSources || card.immuneSources.length === 0) return;
  card.immuneSources.length = 0;
  removeKeyword(card, IMMUNE_KW);
}

function sourceLabel(source) {
  if (!source) return 'неизвестно';
  if (typeof source === 'string') return source;
  if (typeof source === 'object') return source.name_ru || source.id || 'неизвестно';
  return String(source);
}
